import type { SalesOrder, SalesOrderPaymentMode, SalesOrderStatus, OrderPaymentStatus } from '@/types/orders';

/**
 * Single source of truth for which operator actions an order permits.
 *
 * Both the orders grid (quick actions) and the order detail sheet gate on
 * THESE functions, so the two surfaces never disagree and an action is
 * only ever offered when it's genuinely valid for the order's real
 * business state — payment included. Keep all order-action business rules
 * here; do not re-derive them inline.
 */
export interface OrderActionState {
  status: SalesOrderStatus;
  paymentMode: SalesOrderPaymentMode;
  paymentStatus: OrderPaymentStatus;
  /** A live (non-cancelled) invoice already exists for the order. */
  hasInvoice: boolean;
  /** The forward dispatch's status, when one has been materialised. */
  dispatchStatus?: string | null;
  /** The forward dispatch has a booked AWB / tracking number. */
  hasTracking?: boolean;
}

// Statuses from which the status machine permits READY_FOR_DISPATCH.
const READY_FROM: SalesOrderStatus[] = [
  'CREATED',
  'STOCK_RESERVED',
  'PAYMENT_CONFIRMED',
  'PAYMENT_PENDING',
];

/** Convenience adapter — build the action state from a full order row. */
export function orderActionState(o: SalesOrder): OrderActionState {
  return {
    status: o.status,
    paymentMode: o.paymentMode,
    paymentStatus: o.paymentStatus,
    hasInvoice: !!o.invoice,
    dispatchStatus: o.fulfilment?.status ?? null,
    hasTracking: !!o.fulfilment?.trackingNumber,
  };
}

/**
 * Payment obligation satisfied — or deferred by design. COD collects on
 * delivery, so it counts as settled for fulfilment; PREPAID requires the
 * full amount captured (PARTIAL is not enough). This is the gate that
 * stops an unpaid prepaid order being released for shipping or invoiced.
 */
export function isPaymentSettled(o: OrderActionState): boolean {
  return o.paymentMode === 'COD' || o.paymentStatus === 'PAID';
}

/**
 * Generate a tax invoice. Payment is an ABSOLUTE precondition: a PREPAID
 * order must be PAID; COD is settled-on-delivery. Status alone never
 * unlocks it — an unpaid prepaid order that somehow reached
 * READY_FOR_DISPATCH still cannot be invoiced. Never on DRAFT / CANCELLED
 * or when a live invoice already exists.
 */
export function canGenerateInvoice(o: OrderActionState): boolean {
  if (o.status === 'CANCELLED' || o.status === 'DRAFT' || o.hasInvoice) return false;
  return isPaymentSettled(o);
}

/** Print the existing invoice — only when one has been issued. */
export function canPrintInvoice(o: OrderActionState): boolean {
  return o.hasInvoice;
}

/**
 * Mark ready for dispatch (auto-materialises the forward dispatch). The
 * status machine must permit it AND payment must be settled — a PREPAID
 * order is not released for shipping before it's paid; COD is exempt.
 */
export function canMarkReadyForDispatch(o: OrderActionState): boolean {
  return READY_FROM.includes(o.status) && isPaymentSettled(o);
}

/**
 * Mark the order dispatched — only from READY_FOR_DISPATCH, and only once
 * payment is settled. A prepaid order is never released to the customer
 * unpaid (COD is exempt — collected on delivery).
 */
export function canMarkDispatched(o: OrderActionState): boolean {
  return o.status === 'READY_FOR_DISPATCH' && isPaymentSettled(o);
}

/** Pack & verify (scan) — a forward dispatch exists and is still PENDING. */
export function canPackVerify(o: OrderActionState): boolean {
  return o.dispatchStatus === 'PENDING';
}

/** Print the shipping label — the dispatch has a booked AWB. */
export function canPrintLabel(o: OrderActionState): boolean {
  return !!o.hasTracking;
}
